import { situationCardSlotOrder, type SituationSource } from "@peer-start/platform-contracts";
import badgeCheckFill from "../../../assets/icons/badge-check-fill.svg";
import { Button } from "@peer-start/ui";
import { Label, Switch } from "@peer-start/ui/reducate";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../../prototype/api/client.js";
import { useSession } from "../../prototype/api/queries.js";
import { useSituationCard, useSituationCardMutation } from "../api/situation-card-queries.js";
import { AdministrationError, AdministrationLoading } from "../components/AdministrationState.js";
import { SituationCardPageHeading } from "../components/SituationCardPageHeading.js";
import { SituationCardPreview } from "../components/SituationCardPreview.js";
import { SituationCardColumn } from "../components/SituationCardColumn.js";
import { type SectionRevision } from "../components/SituationCardSections.js";
import { SituationConfirmDialog } from "../components/SituationConfirmDialog.js";
import { SituationSourceLibrary } from "../components/SituationSourceLibrary.js";
import { useCardPlayback } from "../components/use-card-playback.js";
import {
  partitionCardSources,
  situationCardCopy,
  situationSourceReferences,
  type SituationCardSection,
} from "./situation-card-view.js";

const toolbarButtonClass = "h-8! min-h-8! gap-1.5! rounded-md! px-2.5! py-0! text-sm! shadow-none!";

const cardSections: readonly SituationCardSection[] = situationCardSlotOrder;

export function AdminSituationCardDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cardId = "" } = useParams();
  const session = useSession();
  const card = useSituationCard(cardId);
  const mutation = useSituationCardMutation();
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [toolbarHost, setToolbarHost] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setToolbarHost(document.getElementById("situation-card-toolbar"));
  }, []);
  const courseSnapshot = useRef<SituationSource[]>([]);
  const playback = useCardPlayback();

  if (card.isLoading) return <AdministrationLoading />;
  if (card.isError || !card.data) return <AdministrationError />;
  const detail = card.data;
  const attachedIds = detail.cardSources.map((source) => source.id);
  const { applied: appliedSources, unapplied } = partitionCardSources(
    detail.cardSources,
    detail.slots,
    detail.canGenerate,
  );
  const unappliedSources =
    detail.status === "review" || detail.status === "processing" ? [] : unapplied;
  const courses = detail.cardSources.filter((source) => source.kind === "cursus");
  if (!detail.canGenerate) courseSnapshot.current = courses;
  const shownCourses = detail.canGenerate ? courseSnapshot.current : courses;
  const liveCopy = situationCardCopy(detail.title, detail.summary, detail.slots);
  const publishedSlots = detail.slots.map((slot) => ({ key: slot.key, text: slot.publishedText }));
  const publishedCopy = situationCardCopy(detail.title, detail.summary, publishedSlots);
  const revisions: Partial<Record<SituationCardSection, SectionRevision>> = {};
  for (const key of cardSections) {
    if (liveCopy[key] !== publishedCopy[key]) {
      revisions[key] = { before: publishedCopy[key], after: liveCopy[key] };
    }
  }
  const shownCopy =
    playback.playback && playback.playback.phase !== "loading" ? playback.playback.paragraphs : liveCopy;
  const generating = playback.playback !== null;
  const contentGenerating =
    playback.playback?.phase === "loading" || playback.playback?.phase === "streaming";
  const showSubmit = detail.canPublish && !contentGenerating;
  const currentUserId = session.data?.authenticated ? session.data.persona.id : undefined;
  const ownChange = currentUserId !== undefined && currentUserId === detail.changedBy.id;
  const readOnly =
    generating ||
    detail.status === "processing" ||
    detail.status === "review" ||
    (detail.status === "concept" && currentUserId !== undefined && !ownChange);
  const awaitingReview = detail.status === "review" && !generating;
  const hasUnsavedChanges = ownChange && (detail.canPublish || detail.canGenerate);

  function leave() {
    if (hasUnsavedChanges) {
      setDiscardOpen(true);
      return;
    }
    navigate("/admin/situation-cards");
  }

  const workflow = awaitingReview ? (
    <>
      <Button
        className={toolbarButtonClass}
        controlId="situation.detail.reopen"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate({ type: "reopen", cardId })}
        type="button"
        variant="secondary"
      >
        {t("administration.situationCard.revise")}
      </Button>
      <Button
        className="h-8! min-h-8! gap-1.5! rounded-md! border-black/10! bg-[var(--chart-4)]! px-3! py-0! text-sm! leading-5! text-inverse! shadow-none! hover:bg-[var(--chart-4)]!"
        controlId="situation.detail.approve"
        disabled={mutation.isPending}
        onClick={() =>
          mutation.mutate(
            { type: "approve", cardId },
            { onSuccess: () => navigate("/admin/situation-cards") },
          )
        }
        type="button"
      >
        <img alt="" src={badgeCheckFill} />
        {t("administration.situationCard.approve")}
      </Button>
    </>
  ) : (
    <Button
      className={toolbarButtonClass}
      controlId="situation.detail.publish"
      disabled={!showSubmit || mutation.isPending}
      onClick={() => setPublishOpen(true)}
      type="button"
    >
      {t("administration.situationCard.submitChange")}
    </Button>
  );
  const displayedCourses =
    shownCourses.length > 0
      ? shownCourses
      : [
          {
            id: `placeholder-course-${detail.id}`,
            title: t("administration.situationCard.placeholderCourse", { title: detail.title }),
            kind: "cursus" as const,
          },
        ];

  function changeSources(sourceIds: string[]) {
    mutation.mutate({ type: "card-sources", cardId, sourceIds });
  }

  function addSource(sourceId: string) {
    if (!sourceId || attachedIds.includes(sourceId) || mutation.isPending) return;
    changeSources([sourceId, ...attachedIds]);
  }

  function removeSource(sourceId: string) {
    if (mutation.isPending) return;
    changeSources(attachedIds.filter((id) => id !== sourceId));
  }

  function acknowledgeUnapplied() {
    if (unappliedSources.length === 0 || mutation.isPending) return;
    mutation.mutate({
      type: "omit-sources",
      cardId,
      sourceIds: unappliedSources.map((source) => source.id),
    });
  }

  function restoreSource(sourceId: string) {
    if (!sourceId || attachedIds.includes(sourceId) || mutation.isPending) return;
    changeSources([...attachedIds, sourceId]);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col max-md:pb-20">
      {toolbarHost
        ? createPortal(
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
              <Button
                className={toolbarButtonClass}
                controlId="situation.detail.cancel"
                disabled={mutation.isPending}
                onClick={leave}
                type="button"
                variant="secondary"
              >
                {t("administration.situationCard.cancel")}
              </Button>
              <div className="flex items-center gap-2">{workflow}</div>
            </div>,
            toolbarHost,
          )
        : null}
      <SituationCardPageHeading
        actions={
          <div className="flex items-center gap-2">
            <Label className="text-xs font-normal text-secondary-text" htmlFor="situation-detail-show-changes">
              {t("administration.situationCard.showChanges")}
            </Label>
            <Switch
              checked={showChanges}
              data-control-id="situation.detail.show-changes"
              id="situation-detail-show-changes"
              onCheckedChange={setShowChanges}
            />
          </div>
        }
        activity={playback.playback?.phase ?? "steady"}
        card={detail}
      />
      {detail.status === "review" ||
      detail.status === "processing" ||
      (detail.status === "concept" && !ownChange && currentUserId) ? (
        <p
          className={`border-b border-border px-4 py-3 text-sm lg:px-6 ${
            detail.status === "processing"
              ? "bg-info text-info-foreground"
              : detail.status === "review"
                ? "bg-warning text-warning-foreground"
                : "bg-muted text-muted-foreground"
          }`}
          role="status"
        >
          {t(
            detail.status === "processing"
              ? "administration.situationCard.processingNotice"
              : detail.status === "review"
                ? "administration.situationCard.reviewNotice"
                : "administration.situationCard.editingNotice",
            { name: detail.changedBy.name },
          )}
        </p>
      ) : null}
      {mutation.isError ? (
        <p className="border-b border-border px-4 py-2 text-sm text-destructive-foreground" role="alert">
          {mutation.error instanceof ApiError ? mutation.error.message : t("common.error")}
        </p>
      ) : null}
      <div className="grid min-h-0 flex-1 gap-8 bg-background p-4 lg:grid-cols-[minmax(24rem,32rem)_minmax(0,1fr)] lg:p-5">
        <SituationCardColumn
          readOnly={readOnly}
          canGenerate={detail.canGenerate}
          generating={contentGenerating}
          onAcknowledgeUnapplied={acknowledgeUnapplied}
          onAddSources={() => setLibraryOpen(true)}
          onGenerate={() => {
            void playback.start(liveCopy, mutation.mutateAsync({ type: "generate", cardId }));
          }}
          showGenerate={!readOnly || generating}
          onRemoveSource={removeSource}
          onRestoreSource={restoreSource}
          pending={mutation.isPending}
          removedSources={detail.removedSources}
          showChanges={showChanges}
          slots={detail.slots}
          sources={appliedSources}
          sourcesChanged={detail.canGenerate}
          unappliedSources={unappliedSources}
        />

        <SituationCardPreview
          courses={displayedCourses}
          dropping={dropping}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
          }}
          onDragOver={(event) => {
            if (readOnly) return;
            event.preventDefault();
            setDropping(true);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDropping(false);
            if (!readOnly) addSource(event.dataTransfer.getData("text/plain"));
          }}
          activity={playback.playback?.phase ?? "steady"}
          onStreamDone={playback.settle}
          revisions={showChanges ? revisions : undefined}
          groundedness={detail.groundedness}
          paragraphs={shownCopy}
          references={situationSourceReferences(appliedSources, detail.slots)}
          title={detail.title}
        />
      </div>
      {libraryOpen ? (
        <SituationSourceLibrary
          attachedIds={attachedIds}
          onClose={() => setLibraryOpen(false)}
          onDragging={setDropping}
          onSave={(sourceIds) => {
            mutation.mutate(
              { type: "card-sources", cardId, sourceIds },
              { onSuccess: () => setLibraryOpen(false) },
            );
          }}
          pending={mutation.isPending}
        />
      ) : null}
      {discardOpen ? (
        <SituationConfirmDialog
          body={t("administration.situationCard.rejectBody")}
          cancelLabel={t("administration.situationCard.backToCard")}
          confirmLabel={t("administration.situationCard.discard")}
          destructive
          onCancel={() => setDiscardOpen(false)}
          onConfirm={() => {
            mutation.mutate(
              { type: "delete-concept", cardId },
              {
                onSuccess: () => {
                  setDiscardOpen(false);
                  navigate("/admin/situation-cards");
                },
              },
            );
          }}
          pending={mutation.isPending}
          title={t("administration.situationCard.rejectTitle")}
        />
      ) : null}
      {publishOpen ? (
        <SituationConfirmDialog
          body={t("administration.situationCard.publishOneBody")}
          confirmLabel={t("administration.situationCard.submitChange")}
          onCancel={() => setPublishOpen(false)}
          onConfirm={() => {
            mutation.mutate(
              { type: "publish", cardId, sourceIds: unapplied.map((source) => source.id) },
              {
                onSuccess: () => {
                  setPublishOpen(false);
                  navigate("/admin/situation-cards");
                },
              },
            );
          }}
          pending={mutation.isPending}
          title={t("administration.situationCard.publishOneTitle")}
        />
      ) : null}
    </div>
  );
}
